import React from "react";
import PropTypes from "prop-types";
import UITextField from "../UITextField";

const controlKeys = [
  "Backspace",
  "Delete",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Enter",
  "Escape",
];

const allowsDecimalValue = (step) =>
  step === "any" || String(step).includes(".");

const allowsNegativeValue = (min) => min === undefined || Number(min) < 0;

// "02" / "-00.5" aren't valid number representations — collapse a run of
// leading zeros, but only when another digit follows (so a lone "0", or
// "0.5" while it's still being typed, is left alone).
const stripLeadingZeros = (value) => value.replace(/^(-?)0+(?=\d)/, "$1");

const normalizeNumberValue = (value, { allowDecimal, allowNegative }) => {
  // Drop the fractional part rather than the separator alone: filtering "." out
  // of a pasted "100.75" would otherwise yield "10075".
  const raw = String(value ?? "");
  const source = allowDecimal ? raw : raw.split(".")[0];
  let hasDecimal = false;

  const filtered = source
    .split("")
    .filter((character, index) => {
      if (/\d/.test(character)) return true;

      if (character === "." && allowDecimal && !hasDecimal) {
        hasDecimal = true;
        return true;
      }

      return character === "-" && allowNegative && index === 0;
    })
    .join("");

  return stripLeadingZeros(filtered);
};

const isAllowedKey = (event, { allowDecimal, allowNegative }) => {
  if (event.ctrlKey || event.metaKey || controlKeys.includes(event.key)) {
    return true;
  }

  if (/^\d$/.test(event.key)) return true;

  if (event.key === ".") {
    return allowDecimal && !String(event.currentTarget.value || "").includes(".");
  }

  return event.key === "-" && allowNegative && event.currentTarget.selectionStart === 0;
};

const UINumberField = ({
  min,
  max,
  step,
  inputMode,
  onKeyDown,
  normalizeValue,
  ...props
}) => {
  const numberOptions = {
    allowDecimal: allowsDecimalValue(step),
    allowNegative: allowsNegativeValue(min),
  };

  return (
    <UITextField
      type="number"
      inputMode={inputMode}
      normalizeValue={(value, event) => {
        const normalizedValue = normalizeNumberValue(value, numberOptions);
        return normalizeValue
          ? normalizeValue(normalizedValue, event)
          : normalizedValue;
      }}
      onKeyDown={(event) => {
        if (!isAllowedKey(event, numberOptions)) {
          event.preventDefault();
          return;
        }

        onKeyDown?.(event);
      }}
      InputProps={{
        inputProps: {
          min,
          max,
          step,
        },
      }}
      {...props}
    />
  );
};

UINumberField.propTypes = {
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inputMode: PropTypes.string,
  onKeyDown: PropTypes.func,
  normalizeValue: PropTypes.func,
};

UINumberField.defaultProps = {
  min: undefined,
  max: undefined,
  step: "any",
  inputMode: "decimal",
  onKeyDown: undefined,
  normalizeValue: undefined,
};

export default UINumberField;
